import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { IconPhone, IconRides } from '../components/icons';
import { Avatar, Empty, ErrorNote, FlashNote, Loading, PageHead, Pill, ReasonDialog, useFlash } from '../components/ui';
import { api } from '../lib/api';
import { count, dateTime, day, phone, REWARD_STATUS_LABEL, REWARD_STATUS_TONE, RIDE_STATUS_LABEL } from '../lib/format';
import { qk } from '../lib/realtime-cache';
import type { AdminRide, EarnedReward, Page, RatingPage, RideStatus, RiderDetail } from '../lib/types';
import { riderRoute } from '../router';
import { Stars, TAG_LABEL } from './Ratings';
import { rewardText } from './Rewards';
import { RideTable } from './Rides';

export function RiderDetailPage() {
  const { riderId } = riderRoute.useParams();
  const qc = useQueryClient();
  const [flash, show] = useFlash();
  const [dialog, setDialog] = useState<'block' | 'unblock' | null>(null);
  const q = useQuery({ queryKey: qk.rider(riderId), queryFn: () => api.get<RiderDetail>(`/admin/riders/${riderId}`) });
  const rides = useQuery({
    queryKey: [...qk.rides, { riderId }],
    queryFn: () => api.get<Page<AdminRide>>('/admin/rides', { riderId, pageSize: 10 }),
  });
  const ratings = useQuery({
    queryKey: [...qk.ratings, { riderId }],
    queryFn: () => api.get<RatingPage>('/admin/ratings', { riderId, byType: 'DRIVER', pageSize: 4 }),
  });
  const rewards = useQuery({
    queryKey: [...qk.rewards, { riderId }],
    queryFn: () => api.get<Page<EarnedReward>>('/admin/rewards', { audience: 'RIDER', subjectId: riderId, pageSize: 10 }),
  });

  if (q.isLoading) return <Loading rows={6} />;
  if (q.error || !q.data)
    return (
      <div className="stack">
        <PageHead title="Rider" back={{ to: '/riders', label: 'Riders' }} />
        <ErrorNote error={q.error} />
      </div>
    );
  const r = q.data;
  const byStatus = Object.entries(r.ridesByStatus) as [RideStatus, number][];

  return (
    <div className="stack">
      <PageHead
        back={{ to: '/riders', label: 'Riders' }}
        title={
          <>
            {r.name ?? 'No name yet'} {r.isBlocked ? <Pill tone="bad">Blocked</Pill> : null}
          </>
        }
        sub={[r.phone ? phone(r.phone) : null, r.email, `joined ${day(r.createdAt)}`].filter(Boolean).join(' · ')}
        actions={
          r.isBlocked ? (
            <button className="btn" onClick={() => setDialog('unblock')}>
              Unblock
            </button>
          ) : (
            <button className="btn btn-danger" onClick={() => setDialog('block')}>
              Block rider
            </button>
          )
        }
      />
      <FlashNote flash={flash} />

      <div className="grid-3">
        <section className="card card-pad stack-sm">
          <span className="overline">Rider</span>
          <div className="person">
            <Avatar name={r.name} large />
            <div style={{ minWidth: 0 }}>
              <div className="strong truncate" style={{ fontSize: 17 }}>
                {r.name ?? 'No name yet'}
              </div>
              <div className="small muted truncate">{r.isBlocked ? 'Blocked' : 'Can book'}</div>
            </div>
          </div>
          <dl className="kv">
            <dt>Phone</dt>
            <dd>{r.phone ? phone(r.phone) : 'None'}</dd>
            <dt>Email</dt>
            <dd>{r.email ?? 'None'}</dd>
            <dt>Rating</dt>
            <dd>{r.rating != null ? `★ ${r.rating.toFixed(1)}` : 'No ratings yet'}</dd>
          </dl>
          {r.phone && (
            <a className="call-btn" href={`tel:${r.phone}`}>
              <IconPhone /> Call rider
            </a>
          )}
        </section>
        <section className="card card-pad stack-sm">
          <span className="overline">Rides</span>
          {byStatus.length ? (
            <dl className="kv">
              {byStatus.map(([s, n]) => (
                <div key={s} style={{ display: 'contents' }}>
                  <dt>{RIDE_STATUS_LABEL[s]}</dt>
                  <dd className="num">{count(n)}</dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="muted">No rides yet.</p>
          )}
        </section>
        <section className="card card-pad stack-sm">
          <span className="overline">Trusted contacts</span>
          {r.trustedContacts.length ? (
            r.trustedContacts.map((c) => (
              <div key={c.id} className="spread">
                <span className="strong">{c.name}</span>
                <a className="link num" href={`tel:${c.phone}`}>
                  {phone(c.phone)}
                </a>
              </div>
            ))
          ) : (
            <p className="muted">None added.</p>
          )}
        </section>
      </div>

      <div className="grid-2">
        <section className="card card-pad stack-sm">
          <span className="overline">What drivers say</span>
          {ratings.data?.items.length ? (
            ratings.data.items.map((x) => (
              <div key={x.id} className="stack-xs">
                <div className="row" style={{ gap: 8 }}>
                  <Stars n={x.stars} />
                  <span className="small faint">
                    {x.driver?.name ?? 'Driver'} · {dateTime(x.createdAt)}
                  </span>
                </div>
                {(x.tags.length > 0 || x.comment) && (
                  <div className="small muted">
                    {x.tags.map((t) => TAG_LABEL[t] ?? t).join(', ')}
                    {x.comment ? ` · “${x.comment}”` : ''}
                  </div>
                )}
              </div>
            ))
          ) : (
            <p className="muted">No ratings yet.</p>
          )}
        </section>
        <section className="card card-pad stack-sm">
          <span className="overline">Rewards earned</span>
          {rewards.data?.items.length ? (
            rewards.data.items.map((x) => (
              <div key={x.id} className="spread" style={{ flexWrap: 'nowrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div className="strong truncate">{x.title}</div>
                  <div className="small muted">
                    {rewardText(x.kind, x.amountPaise, x.percent, x.maxPaise)} · {day(x.createdAt)}
                  </div>
                </div>
                <Pill tone={REWARD_STATUS_TONE[x.status]}>{REWARD_STATUS_LABEL[x.status]}</Pill>
              </div>
            ))
          ) : (
            <p className="muted">None yet.</p>
          )}
        </section>
      </div>

      <section className="card">
        <div className="card-head">
          <h2>Recent rides</h2>
          <span className="small muted">Last 10</span>
        </div>
        {rides.isLoading ? (
          <Loading rows={2} />
        ) : rides.data?.items.length ? (
          <RideTable rides={rides.data.items} showRider={false} />
        ) : (
          <Empty title="No rides yet" icon={IconRides} />
        )}
      </section>

      {dialog && (
        <ReasonDialog
          title={dialog === 'block' ? 'Block this rider?' : 'Unblock this rider?'}
          body={dialog === 'block' ? 'They are signed out everywhere and can’t book until unblocked.' : 'They can sign in and book again.'}
          confirmLabel={dialog === 'block' ? 'Block' : 'Unblock'}
          danger={dialog === 'block'}
          required={dialog === 'block'}
          onClose={() => setDialog(null)}
          onConfirm={async (reason) => {
            await api.post(`/admin/riders/${r.id}/${dialog}`, reason ? { reason } : {});
            show(dialog === 'block' ? 'Rider blocked.' : 'Rider unblocked.');
            void qc.invalidateQueries({ queryKey: qk.rider(r.id) });
            void qc.invalidateQueries({ queryKey: qk.riders });
          }}
        />
      )}
    </div>
  );
}
